import React, { useEffect, useRef } from 'react';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import VideoProgressManager from './videoProgress.js';

const videoProgressManager = new VideoProgressManager();

const RelatedVideosCarousel = ({ videos, onVideoSelect, title = 'Related Videos' }) => {
    const swiperRef = useRef(null);
    const swiperInstanceRef = useRef(null);

    useEffect(() => {
        if (swiperRef.current && videos.length > 0) {
            // Destroy existing Swiper instance if it exists
            if (swiperInstanceRef.current) {
                swiperInstanceRef.current.destroy();
            }

            // Initialize new Swiper instance
            swiperInstanceRef.current = new Swiper(swiperRef.current, {
                modules: [Navigation, Pagination],
                spaceBetween: 20,
                slidesPerView: 'auto',
                slidesOffsetBefore: 20,
                slidesOffsetAfter: 20,
                navigation: {
                    nextEl: '.swiper-button-next',
                    prevEl: '.swiper-button-prev',
                },
                pagination: {
                    el: '.swiper-pagination',
                    clickable: true
                },
                breakpoints: {
                    768: {
                        spaceBetween: 24,
                        slidesOffsetBefore: 20,
                        slidesOffsetAfter: 20,
                    },
                    1024: {
                        spaceBetween: 24,
                        slidesOffsetBefore: 96,
                        slidesOffsetAfter: 96,
                    },
                    1280: {
                        spaceBetween: 24,
                        slidesOffsetBefore: 144,
                        slidesOffsetAfter: 144,
                    },
                    1680: {
                        spaceBetween: 32,
                        slidesOffsetBefore: 144,
                        slidesOffsetAfter: 144,
                    },
                },
                on: {
                    init: function() {
                        // Calculate slide widths after initialization
                        calculateSlideWidths();
                    },
                    resize: function() {
                        calculateSlideWidths();
                    }
                }
            });
        }

        // Cleanup function
        return () => {
            if (swiperInstanceRef.current) {
                swiperInstanceRef.current.destroy();
                swiperInstanceRef.current = null;
            }
        };
    }, [videos]);

    useEffect(() => {
        if (videos && videos.length > 0) {
            setTimeout(() => {
                videoProgressManager.updateProgressBars();
            }, 100);
        }
    }, [videos]);

    // Calculate slide widths based on container size
    const calculateSlideWidths = () => {
        const carouselElement = swiperRef.current;
        if (!carouselElement) return;

        const containerWidth = carouselElement.offsetWidth;
        const windowWidth = window.innerWidth;

        // Don't calculate for mobile (below 1024px)
        if (windowWidth < 1024) return;

        // Determine spacing based on screen size
        let spaceBetween = 20;
        if (windowWidth >= 1680) {
            spaceBetween = 32;
        } else if (windowWidth >= 1280) {
            spaceBetween = 24;
        }

        // Always 4 slides as requested
        const slidesPerContainer = 4;

        // Calculate slide width
        const totalSpacing = spaceBetween * (slidesPerContainer - 1);
        const availableWidth = containerWidth - totalSpacing;
        const slideWidth = availableWidth / slidesPerContainer;

        // Set CSS custom properties
        carouselElement.style.setProperty('--slide-width', `${slideWidth}px`);
        carouselElement.style.setProperty('--slides-per-view', slidesPerContainer.toString());
    };

    // Handle window resize
    useEffect(() => {
        const handleResize = () => {
            calculateSlideWidths();
            if (swiperInstanceRef.current) {
                swiperInstanceRef.current.update();
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    if (!videos || videos.length === 0) {
        return null;
    }

    return (
        <div className="versatile-carousel">
            <div className="versatile-carousel__wrapper grid full">
                <div className="versatile-carousel__heading">
                    <div className="versatile-carousel__heading--title">
                        <h2 className="h4">{title}</h2>
                    </div>
                </div>

                <div className="versatile-carousel__top">
                    <div className="swiper-arrows">
                        <button className="swiper-button-prev" aria-label="Previous">
                            <svg width="42" height="42" viewBox="0 0 42 42" fill="currentColor">
                                <path d="M18.047 12.789L25.156 21l-7.109 8.211-1.203-1.422L22.722 21l-5.878-6.789 1.203-1.422z"/>
                            </svg>
                        </button>
                        <button className="swiper-button-next" aria-label="Next">
                            <svg width="42" height="42" viewBox="0 0 42 42" fill="currentColor">
                                <path d="M18.047 12.789L25.156 21l-7.109 8.211-1.203-1.422L22.722 21l-5.878-6.789 1.203-1.422z"/>
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="swiper" ref={swiperRef}>
                    <div className="swiper-wrapper size-4">
                        {videos.map((video) => (
                            <div key={video.id} className="swiper-slide">
                                <div className="card-tv">
                                    {video.thumbnail && (
                                        <a
                                            className="card-tv__media"
                                            href={`/fstv/${video.slug}/`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                onVideoSelect(video.slug);
                                            }}
                                        >
                                            <figure className="card-tv__media--thumb">
                                                <img
                                                    src={video.thumbnail}
                                                    alt={video.title}
                                                    loading="lazy"
                                                />
                                            </figure>
                                            <div className="tv-progress" data-id={video.youtube_id}>
                                                <div className="tv-bar"></div>
                                            </div>
                                        </a>
                                    )}
                                    <div className="card-tv__content">
                                        {(video.topic || (video.duration && video.duration !== '00:00')) && (
                                            <div className="card-tv__content--meta">
                                                {video.topic && (
                                                    <span className="tag outline small">{video.topic}</span>
                                                )}
                                                {video.duration && video.duration !== '00:00' && (
                                                    <div className="card-tv__meta--duration">{video.duration}</div>
                                                )}
                                            </div>
                                        )}
                                        <a
                                            className="card-tv__content--title"
                                            href={`/fstv/${video.slug}/`}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                onVideoSelect(video.slug);
                                            }}
                                        >
                                            <h3>{video.title}</h3>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
                <div className="versatile-carousel__spacer"></div>
            </div>
        </div>
    );
};

export default RelatedVideosCarousel;
